import React, { useEffect, useMemo, useState } from 'react';
import {
  getDiagnosticsSummary,
  getDiagnosticsConcurrency,
  getDiagnosticsOperations,
  getDiagnosticsRateWindow,
  getDiagnosticsEvents,
  getDiagnosticsSites,
  getDiagnosticsCorrelation,
} from '../api';
import { useAuth } from '../auth/AuthContext';
import { hasTelemetryForPeriod, submissionsProcessingText, timezoneSummary } from '../diagnosticsDisplay';
import './DashboardPage.css';

const PERIODS = [
  ['today', 'Today'],
  ['last_7_days', 'Last 7 Days'],
  ['last_30_days', 'Last 30 Days'],
  ['this_month', 'This Month'],
  ['this_year', 'This Year'],
  ['custom', 'Custom'],
];

function paramsFor(period, siteId, customFrom, customTo, extra = {}) {
  const params = { period, ...extra };
  if (siteId) params.siteId = siteId;
  if (period === 'custom') {
    if (customFrom) params.from = customFrom;
    if (customTo) params.to = customTo;
  }
  return params;
}

function fmtMs(value) {
  const n = Number(value || 0);
  if (!n) return '0 ms';
  return n >= 1000 ? `${(n / 1000).toFixed(2)} s` : `${Math.round(n)} ms`;
}

function fmtHour(hour) {
  if (hour === null || hour === undefined) return '-';
  return `${String(hour).padStart(2, '0')}:00 UTC`;
}

function BarList({ rows, valueKey, labelKey, emptyText }) {
  const max = Math.max(...(rows || []).map(row => Number(row[valueKey] || 0)), 1);
  if (!rows || rows.length === 0) return <div className="text-muted" style={{ padding: 16 }}>{emptyText}</div>;
  return (
    <div className="analytics-trend-list">
      {rows.map((row, index) => {
        const value = Number(row[valueKey] || 0);
        return (
          <div key={`${row[labelKey]}-${index}`} className="analytics-trend-row">
            <span className="font-mono text-sm">{String(row[labelKey] || '-').slice(0, 24)}</span>
            <div className="analytics-trend-track"><div className="analytics-trend-fill" style={{ width: `${Math.max(2, (value / max) * 100)}%` }} /></div>
            <strong className="text-right">{value}</strong>
          </div>
        );
      })}
    </div>
  );
}

export default function DiagnosticsPage() {
  const { user } = useAuth();
  const isAdmin = String(user?.role || '').trim().toLowerCase() === 'admin';
  const [period, setPeriod] = useState('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [siteId, setSiteId] = useState('');
  const [sites, setSites] = useState([]);
  const [summary, setSummary] = useState(null);
  const [concurrency, setConcurrency] = useState(null);
  const [operations, setOperations] = useState([]);
  const [rateWindow, setRateWindow] = useState(null);
  const [events, setEvents] = useState({ rows: [], total: 0 });
  const [severity, setSeverity] = useState('');
  const [eventType, setEventType] = useState('');
  const [correlationId, setCorrelationId] = useState('');
  const [correlation, setCorrelation] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const baseParams = useMemo(() => paramsFor(period, siteId, customFrom, customTo), [period, siteId, customFrom, customTo]);
  const canLoad = period !== 'custom' || (customFrom && customTo);

  useEffect(() => {
    if (!isAdmin) return;
    getDiagnosticsSites().then(res => setSites(res.data.rows || [])).catch(() => setSites([]));
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin || !canLoad) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [summaryRes, concurrencyRes, operationsRes, rateWindowRes, eventsRes] = await Promise.all([
          getDiagnosticsSummary(baseParams),
          getDiagnosticsConcurrency(baseParams),
          getDiagnosticsOperations(baseParams),
          getDiagnosticsRateWindow(baseParams),
          getDiagnosticsEvents({ ...baseParams, severity: severity || undefined, eventType: eventType || undefined, pageSize: 50 }),
        ]);
        if (cancelled) return;
        setSummary(summaryRes.data);
        setConcurrency(concurrencyRes.data);
        setOperations(operationsRes.data.rows || []);
        setRateWindow(rateWindowRes.data);
        setEvents(eventsRes.data || { rows: [], total: 0 });
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || 'Failed to load diagnostics');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const timer = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [baseParams, canLoad, eventType, isAdmin, severity]);

  const eventTypes = useMemo(() => [...new Set((events.rows || []).map(row => row.EventType).filter(Boolean))].sort(), [events.rows]);
  const hasTelemetry = hasTelemetryForPeriod(summary, events.total, operations, concurrency);
  const timezones = timezoneSummary();
  const siteRows = useMemo(() => {
    const counts = new Map();
    for (const row of events.rows || []) {
      const label = row.SiteCode || (row.SiteId ? `Site ${row.SiteId}` : 'Unknown');
      counts.set(label, (counts.get(label) || 0) + 1);
    }
    return [...counts.entries()].map(([site, total]) => ({ site, total }));
  }, [events.rows]);

  if (!isAdmin) {
    return (
      <>
        <div className="top-header"><h1>Diagnostics</h1></div>
        <div className="page-content"><div className="hs-card"><div className="hs-card-body">Diagnostics require authenticated admin access.</div></div></div>
      </>
    );
  }

  const totals = summary?.totals || {};
  const hubspot = summary?.hubspot || {};
  const performance = summary?.performance || {};
  const live = summary?.live || {};
  const busiest = summary?.busiest || {};

  return (
    <>
      <div className="top-header">
        <h1>Admin Diagnostics</h1>
        <div className="top-header-actions"><span className="text-muted text-sm">UTC telemetry, read-only, refreshes every 60 seconds</span></div>
      </div>
      <div className="page-content">
        <div className="hs-card mb-lg">
          <div className="hs-card-header"><h2>Filters</h2></div>
          <div className="analytics-card-body analytics-filter-grid">
            <div><label className="text-sm text-muted">Period</label><select className="hs-input" value={period} onChange={e => setPeriod(e.target.value)}>{PERIODS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div>
            {period === 'custom' && <><div><label className="text-sm text-muted">From</label><input className="hs-input" type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} /></div><div><label className="text-sm text-muted">To</label><input className="hs-input" type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} /></div></>}
            <div><label className="text-sm text-muted">Site</label><select className="hs-input" value={siteId} onChange={e => setSiteId(e.target.value)}><option value="">All Sites</option>{sites.map(site => <option key={site.SiteId} value={site.SiteId}>{site.SiteCode} - {site.SiteName}</option>)}</select></div>
          </div>
          {error && <div className="text-danger" style={{ padding: '0 16px 16px' }}>{error}</div>}
        </div>

        {loading && <div className="hs-card mb-md"><div className="hs-card-body">Loading diagnostics...</div></div>}
        {!loading && (!summary || !hasTelemetry) && <div className="hs-card mb-md"><div className="hs-card-body text-muted">No telemetry available for this period.</div></div>}
        <div className="text-muted text-sm mb-md">Storage: {timezones.storage}. Grouping: {timezones.grouping}. Display: {timezones.display}.</div>

        <div className="analytics-kpi-grid">
          <div className="stat-card"><div className="stat-value">{live.activeSessionsNow || 0}</div><div className="stat-label">Active Sessions</div></div>
          <div className="stat-card"><div className="stat-value">{live.activeSitesNow || 0}</div><div className="stat-label">Active Sites</div></div>
          <div className="stat-card"><div className="stat-value" style={{ fontSize: 18 }}>{submissionsProcessingText(live.submissionsProcessing)}</div><div className="stat-label">Submissions Processing</div></div>
          <div className="stat-card"><div className="stat-value">{totals.sessionStarts || 0}</div><div className="stat-label">Session Starts</div></div>
          <div className="stat-card"><div className="stat-value">{totals.openOperations || 0}</div><div className="stat-label">OPEN Operations</div></div>
          <div className="stat-card"><div className="stat-value">{totals.submitAttempts || 0}</div><div className="stat-label">SUBMIT Attempts</div></div>
          <div className="stat-card"><div className="stat-value">{totals.successfulSubmits || 0}</div><div className="stat-label">Successful Submits</div></div>
          <div className="stat-card"><div className="stat-value">{totals.failedSubmits || 0}</div><div className="stat-label">Failed Submits</div></div>
          <div className="stat-card"><div className="stat-value">{totals.peakConcurrentSessions || 0}</div><div className="stat-label">Peak Sessions</div></div>
        </div>

        <div className="analytics-kpi-grid">
          <div className="stat-card"><div className="stat-value">{hubspot.totalRequests || 0}</div><div className="stat-label">HubSpot Requests</div></div>
          <div className="stat-card"><div className="stat-value">{hubspot.avgRequestsPerOpen || 0}</div><div className="stat-label">Avg / OPEN</div></div>
          <div className="stat-card"><div className="stat-value">{hubspot.avgRequestsPerSubmit || 0}</div><div className="stat-label">Avg / SUBMIT</div></div>
          <div className="stat-card"><div className="stat-value">{hubspot.responses429 || 0}</div><div className="stat-label">429 Responses</div></div>
          <div className="stat-card"><div className="stat-value">{hubspot.retries || 0}</div><div className="stat-label">Retries</div></div>
          <div className="stat-card"><div className="stat-value">{hubspot.failedHubSpotOperations || 0}</div><div className="stat-label">HubSpot Failures</div></div>
          <div className="stat-card"><div className="stat-value">{rateWindow?.averageRequestsPer10Seconds || 0}</div><div className="stat-label">Avg Requests / 10s</div></div>
          <div className="stat-card"><div className="stat-value">{rateWindow?.peakRequestsPer10Seconds || 0}</div><div className="stat-label">Peak Requests / 10s</div></div>
          <div className="stat-card"><div className="stat-value">{fmtMs(performance.avgOpenDurationMs)}</div><div className="stat-label">Avg OPEN</div></div>
          <div className="stat-card"><div className="stat-value">{fmtMs(performance.p95SubmitDurationMs)}</div><div className="stat-label">P95 SUBMIT</div></div>
        </div>

        <div className="stats-row">
          <div className="hs-card" style={{ flex: 1 }}><div className="hs-card-header"><h2>HubSpot 10-Second Rate Window</h2></div><div className="hs-card-body">
            <div className="analytics-summary-grid">
              <div className="analytics-summary-item"><span className="text-muted text-sm">Configured limit</span><strong>{rateWindow?.configuredLimit || 100} requests / 10s</strong></div>
              <div className="analytics-summary-item"><span className="text-muted text-sm">Peak utilization</span><strong>{rateWindow?.peakUtilizationPercent || 0}%</strong></div>
              <div className="analytics-summary-item"><span className="text-muted text-sm">Busiest window</span><strong>{rateWindow?.peakWindowStart && rateWindow?.peakWindowEnd ? `${new Date(rateWindow.peakWindowStart).toLocaleTimeString()} - ${new Date(rateWindow.peakWindowEnd).toLocaleTimeString()}` : '-'}</strong></div>
              <div className="analytics-summary-item"><span className="text-muted text-sm">Windows with 429</span><strong>{rateWindow?.windowsWith429 || 0}</strong></div>
              <div className="analytics-summary-item"><span className="text-muted text-sm">Sampled windows</span><strong>{rateWindow?.windowsSampled || 0}</strong></div>
            </div>
            <div className="text-muted text-sm" style={{ marginTop: 12 }}>Each value is calculated from recorded HubSpot request counts in a rolling 10-second window. The configured limit is adjustable with HUBSPOT_REQUESTS_PER_10_SECONDS_LIMIT.</div>
          </div></div>
        </div>

        <div className="stats-row">
          <div className="hs-card" style={{ flex: 1 }}><div className="hs-card-header"><h2>Busiest Periods</h2></div><div className="hs-card-body">
            <div className="analytics-summary-grid">
              <div className="analytics-summary-item"><span className="text-muted text-sm">Session Hour</span><strong>{fmtHour(busiest.BusiestSessionHour)} ({busiest.BusiestSessionHourCount || 0})</strong></div>
              <div className="analytics-summary-item"><span className="text-muted text-sm">OPEN Hour</span><strong>{fmtHour(busiest.BusiestOpenHour)} ({busiest.BusiestOpenHourCount || 0})</strong></div>
              <div className="analytics-summary-item"><span className="text-muted text-sm">SUBMIT Hour</span><strong>{fmtHour(busiest.BusiestSubmitHour)} ({busiest.BusiestSubmitHourCount || 0})</strong></div>
              <div className="analytics-summary-item"><span className="text-muted text-sm">Weekday</span><strong>{busiest.BusiestWeekday || '-'}</strong></div>
              <div className="analytics-summary-item"><span className="text-muted text-sm">Date</span><strong>{busiest.BusiestDate ? String(busiest.BusiestDate).slice(0, 10) : '-'}</strong></div>
              <div className="analytics-summary-item"><span className="text-muted text-sm">Month</span><strong>{busiest.BusiestMonth ? String(busiest.BusiestMonth).slice(0, 7) : '-'}</strong></div>
            </div>
          </div></div>
        </div>

        <div className="stats-row">
          <div className="hs-card" style={{ flex: 1 }}><div className="hs-card-header"><h2>Concurrency Over Time</h2></div><div className="analytics-card-body"><BarList rows={(concurrency?.series || []).map(r => ({ bucket: String(r.bucketStart).slice(0, 16), total: r.concurrentSessions }))} labelKey="bucket" valueKey="total" emptyText="No session concurrency data for this period." /></div></div>
          <div className="hs-card" style={{ flex: 1 }}><div className="hs-card-header"><h2>Operations by Hour</h2></div><div className="analytics-card-body"><BarList rows={operations.map(r => ({ bucket: String(r.BucketStart).slice(0, 16), total: (r.Opens || 0) + (r.Submits || 0) }))} labelKey="bucket" valueKey="total" emptyText="No operations for this period." /></div></div>
        </div>

        <div className="stats-row">
          <div className="hs-card" style={{ flex: 1 }}><div className="hs-card-header"><h2>HubSpot Traffic</h2></div><div className="analytics-card-body"><BarList rows={operations.map(r => ({ bucket: String(r.BucketStart).slice(0, 16), total: r.HubSpotRequests || 0 }))} labelKey="bucket" valueKey="total" emptyText="No HubSpot traffic for this period." /></div></div>
          <div className="hs-card" style={{ flex: 1 }}><div className="hs-card-header"><h2>Activity by Site</h2></div><div className="analytics-card-body"><BarList rows={siteRows} labelKey="site" valueKey="total" emptyText="No site event activity for this period." /></div></div>
        </div>

        <div className="hs-card mb-lg">
          <div className="hs-card-header"><h2>Recent Events</h2></div>
          <div className="analytics-card-body analytics-filter-grid">
            <div><label className="text-sm text-muted">Severity</label><select className="hs-input" value={severity} onChange={e => setSeverity(e.target.value)}><option value="">All</option><option>INFO</option><option>WARN</option><option>ERROR</option><option>CRITICAL</option></select></div>
            <div><label className="text-sm text-muted">Event Type</label><select className="hs-input" value={eventType} onChange={e => setEventType(e.target.value)}><option value="">All</option>{eventTypes.map(type => <option key={type}>{type}</option>)}</select></div>
          </div>
          {selectedEvent && <div className="analytics-card-body" style={{ borderTop: '1px solid var(--border-color, #dbe3ea)', background: '#f8fafc' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start' }}>
              <div><strong>Error details</strong><div className="text-muted text-sm">Click another event to inspect it.</div></div>
              <button className="hs-btn hs-btn-link hs-btn-sm" onClick={() => setSelectedEvent(null)}>Close</button>
            </div>
            <div className="analytics-summary-grid" style={{ marginTop: 12 }}>
              <div className="analytics-summary-item"><span className="text-muted text-sm">Message</span><strong>{selectedEvent.ErrorMessage || 'No message recorded'}</strong></div>
              <div className="analytics-summary-item"><span className="text-muted text-sm">Date and time</span><strong>{selectedEvent.CreatedAt ? new Date(selectedEvent.CreatedAt).toLocaleString() : '-'}</strong></div>
              <div className="analytics-summary-item"><span className="text-muted text-sm">Source</span><strong>{selectedEvent.Source || 'Unknown source'}</strong></div>
              <div className="analytics-summary-item"><span className="text-muted text-sm">User</span><strong>{selectedEvent.UserDisplayName || selectedEvent.UserEmail || selectedEvent.UserId || 'System'}</strong></div>
              <div className="analytics-summary-item"><span className="text-muted text-sm">Event</span><strong>{selectedEvent.EventType || '-'}</strong></div>
              <div className="analytics-summary-item"><span className="text-muted text-sm">Severity</span><strong>{selectedEvent.Severity || '-'}</strong></div>
            </div>
          </div>}
          <div className="analytics-table-wrap"><table className="hs-table analytics-table"><thead><tr><th>Time</th><th>Type</th><th>Severity</th><th>Error</th><th>Site</th><th>HS Order ID</th><th>Correlation</th></tr></thead><tbody>{(events.rows || []).length === 0 ? <tr><td colSpan="7" className="text-center text-muted" style={{ padding: 24 }}>No telemetry events for this period.</td></tr> : events.rows.map(row => <tr key={row.EventId} onClick={() => setSelectedEvent(row)} title={row.ErrorMessage || 'Click to view event details'} style={{ cursor: 'pointer' }}><td className="font-mono text-sm">{new Date(row.CreatedAt).toLocaleString()}</td><td>{row.EventType}</td><td>{row.Severity}</td><td>{row.ErrorCategory || '-'}</td><td>{row.SiteCode || row.SiteId || '-'}</td><td>{row.HubSpotOrderId || '-'}</td><td><button className="hs-btn hs-btn-link hs-btn-sm" onClick={e => { e.stopPropagation(); setCorrelationId(row.CorrelationId || ''); }}>{row.CorrelationId ? row.CorrelationId.slice(0, 8) : '-'}</button></td></tr>)}</tbody></table></div>
        </div>

        <div className="hs-card">
          <div className="hs-card-header"><h2>Correlation Drilldown</h2></div>
          <div className="analytics-card-body" style={{ display: 'flex', gap: 8 }}><input className="hs-input" value={correlationId} onChange={e => setCorrelationId(e.target.value)} placeholder="CorrelationId" /><button className="hs-btn hs-btn-primary" onClick={async () => { if (!correlationId) return; const res = await getDiagnosticsCorrelation(correlationId); setCorrelation(res.data); }}>Load</button></div>
          {correlation && <div className="analytics-card-body"><pre style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{JSON.stringify(correlation, null, 2)}</pre></div>}
        </div>
      </div>
    </>
  );
}